import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';
import { superYachts } from '../../../mocks/superYachts';

export default function FlyingFoxPage() {
  const navigate = useNavigate();
  const [isNavVisible, setIsNavVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showInquiryForm, setShowInquiryForm] = useState(false);

  const yacht = superYachts.find(y => y.slug === 'flying-fox');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsNavVisible(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!yacht) {
    return <div>Yacht not found</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation isVisible={isNavVisible} setIsVisible={setIsNavVisible} />
      
      {/* Hero Section */}
      <section className="relative h-screen">
        <div className="absolute inset-0">
          <img
            src={yacht.images[selectedImage]}
            alt={yacht.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
        </div>
        
        <div className="relative h-full flex flex-col justify-between">
          {/* Back Button */}
          <div className="pt-24 px-8">
            <button
              onClick={() => navigate('/yacht-charter')}
              className="flex items-center gap-2 text-white hover:text-white/80 transition-colors group"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="text-sm font-medium">Back to Charter Fleet</span>
            </button>
          </div>

          {/* Yacht Info */}
          <div className="px-8 pb-16 text-white">
            <div className="max-w-7xl mx-auto">
              <h1 className="text-6xl md:text-7xl font-light mb-4 tracking-tight">{yacht.name}</h1>
              <div className="flex flex-wrap items-center gap-6 text-lg mb-8">
                <span className="flex items-center gap-2">
                  <i className="ri-ship-line"></i>
                  {yacht.lengthFeet}ft / {yacht.length}m
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-calendar-line"></i>
                  {yacht.year}
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-building-line"></i>
                  {yacht.builder}
                </span>
              </div>
              
              <div className="flex flex-wrap gap-8 text-sm">
                <div>
                  <div className="text-white/70 mb-1">Guests</div>
                  <div className="text-2xl font-light">{yacht.guestCapacity}</div>
                </div>
                <div>
                  <div className="text-white/70 mb-1">Cabins</div>
                  <div className="text-2xl font-light">{yacht.cabins}</div>
                </div>
                <div>
                  <div className="text-white/70 mb-1">Crew</div>
                  <div className="text-2xl font-light">{yacht.crew}</div>
                </div>
                <div>
                  <div className="text-white/70 mb-1">Weekly Rate</div>
                  <div className="text-2xl font-light">${yacht.weeklyRate.toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-6 gap-4">
            {yacht.images.map((image, index) => (
              <button
                key={index}
                onClick={() => setSelectedImage(index)}
                className={`relative aspect-video rounded-lg overflow-hidden transition-all ${
                  selectedImage === index ? 'ring-4 ring-teal-500 scale-105' : 'hover:scale-105'
                }`}
              >
                <img
                  src={image}
                  alt={`${yacht.name} ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-8">
          <h2 className="text-4xl font-light mb-8">Overview</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            {yacht.description}
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            <div>
              <h3 className="text-2xl font-light mb-4">Specifications</h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Builder</span>
                  <span className="font-medium">{yacht.specifications.builder}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Year Built</span>
                  <span className="font-medium">{yacht.specifications.yearBuilt}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Length</span>
                  <span className="font-medium">{yacht.lengthFeet}ft / {yacht.length}m</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Beam</span>
                  <span className="font-medium">{yacht.specifications.beam}m</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Draft</span>
                  <span className="font-medium">{yacht.specifications.draft}m</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Cruising Speed</span>
                  <span className="font-medium">{yacht.specifications.cruisingSpeed} knots</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Max Speed</span>
                  <span className="font-medium">{yacht.specifications.maxSpeed} knots</span>
                </div>
                {yacht.specifications.engines && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Engines</span>
                    <span className="font-medium">{yacht.specifications.engines}</span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-light mb-4">Accommodation</h3>
              <div className="space-y-3 mb-8">
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Guest Capacity</span>
                  <span className="font-medium">{yacht.guestCapacity} guests</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Cabins</span>
                  <span className="font-medium">{yacht.cabins} suites</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Crew</span>
                  <span className="font-medium">{yacht.crew} professionals</span>
                </div>
              </div>

              <h3 className="text-2xl font-light mb-4 mt-8">Charter Details</h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Weekly Rate</span>
                  <span className="font-medium">${yacht.weeklyRate.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Cruising Region</span>
                  <span className="font-medium">{yacht.cruisingRegion}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Available</span>
                  <span className="font-medium">{yacht.availableSeasons.join(', ')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-8">
          <h2 className="text-4xl font-light mb-12">Key Features</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {yacht.highlights.map((highlight, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-check-line text-2xl text-teal-500"></i>
                </div>
                <p className="text-gray-700 flex-1">{highlight}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <h2 className="text-4xl font-light mb-6">Ready to Charter {yacht.name}?</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Contact our charter specialists to discuss your requirements and create an unforgettable experience aboard this magnificent superyacht.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="#contact"
              className="px-8 py-4 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
            >
              Request Charter Information
            </a>
            <button
              onClick={() => navigate('/yacht-charter')}
              className="px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-lg hover:border-gray-400 transition-colors whitespace-nowrap"
            >
              View More Yachts
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
